import React, { useState } from 'react';
import { X, Wand2, Loader2 } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import { useAuthenticatedFetch } from '../utils/api';
import { useAuth } from '@clerk/clerk-react';

const ColorPreview = ({ colors }) => {
  if (!colors) return null;

  return (
    <div className="space-y-4">
      <h3 className="text-sm font-medium text-theme">Preview</h3>
      <div className="p-4 rounded-lg" style={{ backgroundColor: colors.background }}>
        <div className="space-y-4">
          {/* Text Preview */}
          <div style={{ color: colors.text }}>
            <h4 className="text-lg font-semibold">Sample Text</h4>
            <p>This is how your theme's text will look.</p>
          </div>

          {/* Button Preview */}
          <div className="space-x-2">
            <button
              className="px-4 py-2 rounded-lg transition-colors duration-200"
              style={{ 
                backgroundColor: colors.primary,
                color: colors.text,
              }}
              onMouseOver={e => e.target.style.backgroundColor = colors.primaryHover}
              onMouseOut={e => e.target.style.backgroundColor = colors.primary}
            >
              Primary Button
            </button>
            <button
              className="px-4 py-2 rounded-lg transition-colors duration-200"
              style={{ 
                backgroundColor: colors.secondary,
                color: colors.accent,
                border: `1px solid ${colors.border}`
              }}
              onMouseOver={e => e.target.style.backgroundColor = colors.hover}
              onMouseOut={e => e.target.style.backgroundColor = colors.secondary}
            >
              Secondary Button
            </button>
          </div>

          {/* Status Colors */}
          <div className="flex gap-2">
            <div 
              className="px-3 py-1 rounded-full text-sm"
              style={{ backgroundColor: colors.error, color: colors.background }}
            >
              Error State
            </div>
            <div 
              className="px-3 py-1 rounded-full text-sm"
              style={{ backgroundColor: colors.success, color: colors.background }}
            >
              Success State
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const CreateThemeModal = ({ isOpen, onClose }) => {
  const [mode, setMode] = useState('light');
  const [description, setDescription] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState('');
  const [generatedTheme, setGeneratedTheme] = useState(null);
  const { setTheme, theme } = useTheme();
  const authenticatedFetch = useAuthenticatedFetch();
  const { isLoaded, isSignedIn } = useAuth();

  const handleGenerate = async () => {
    if (!isLoaded || !isSignedIn) {
      setError('Please sign in to generate themes');
      return;
    }

    setError('');
    setIsGenerating(true);
    
    try {
      const response = await authenticatedFetch(
        'https://classify-alpha-a1e5331e0be0.herokuapp.com/api/generate-theme',
        {
          method: 'POST',
          body: JSON.stringify({ description, mode })
        }
      );

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.details || data.error || 'Failed to generate theme');
      }

      setGeneratedTheme(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleSave = () => {
    if (generatedTheme) {
      const category = mode;
      const themeWithCategory = {
        ...generatedTheme,
        category
      };
      
      setTheme(themeWithCategory);
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-theme border border-theme rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-semibold text-theme">Create Custom Theme</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-secondary rounded-lg transition-colors"
          >
            <X className="text-theme" size={20} />
          </button>
        </div>

        {/* Mode Toggle */}
        <div className="space-y-2">
          <label className="text-sm font-medium text-theme">Theme Mode</label>
          <div className="flex gap-2">
            <button
              onClick={() => setMode('light')}
              className={`flex-1 px-4 py-2 rounded-lg transition-colors ${
                mode === 'light'
                  ? 'bg-primary text-theme'
                  : 'bg-secondary text-accent hover:bg-primary hover:text-theme'
              }`}
            >
              Light
            </button>
            <button
              onClick={() => setMode('dark')}
              className={`flex-1 px-4 py-2 rounded-lg transition-colors ${
                mode === 'dark'
                  ? 'bg-primary text-theme'
                  : 'bg-secondary text-accent hover:bg-primary hover:text-theme'
              }`}
            >
              Dark
            </button>
          </div>
        </div>

        {/* Description Input */}
        <div className="space-y-2">
          <label className="text-sm font-medium text-theme">Theme Description</label>
          <div className="relative">
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Describe your desired theme (e.g., 'A calming ocean theme with soft blues and gentle greens')"
              className="w-full px-4 py-3 rounded-lg border border-theme bg-secondary text-theme placeholder-accent resize-none h-32"
            />
            <button
              onClick={handleGenerate}
              disabled={!description.trim() || isGenerating}
              className={`absolute right-2 bottom-2 px-4 py-2 rounded-lg transition-colors flex items-center gap-2 ${
                !description.trim() || isGenerating
                  ? 'bg-secondary text-accent cursor-not-allowed'
                  : 'bg-primary text-theme hover:bg-primaryHover'
              }`}
            >
              {isGenerating ? (
                <Loader2 className="animate-spin" size={18} />
              ) : (
                <Wand2 size={18} />
              )}
              Generate
            </button>
          </div>
          {error && (
            <p className="text-sm text-error">{error}</p>
          )}
        </div>

        {/* Preview */}
        {generatedTheme && (
          <div className="space-y-4">
            <ColorPreview colors={generatedTheme} />
            <div className="flex justify-end">
              <button
                onClick={handleSave}
                className="px-4 py-2 bg-primary text-theme rounded-lg hover:bg-primaryHover transition-colors"
              >
                Apply Theme
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CreateThemeModal; 